import { useCallback, useEffect, useRef, useState } from 'react';

import type { Aircraft } from '@flightscanner/shared';

import { fetchAircraft } from './apiClient';
import { enrich, type RichAircraft } from './enrich';

/** Poll interval for the focused ICAO — faster than the viewport /api/states cadence. */
const SELECTED_POLL_MS = 3_000;

export interface SelectedTrackState {
  /** Fresher position for the selected hex, when /api/aircraft succeeds. */
  aircraft: RichAircraft | null;
  /** Unix seconds when `aircraft` was last fetched (used for tight extrapolation). */
  fetchedAtSec: number;
}

/**
 * Poll `/api/aircraft/:icao` while a flight is selected so follow-mode uses live
 * positions instead of only the slower bbox snapshot (feeder tiles / DO cache).
 */
export function useSelectedAircraftTrack(selectedIcao: string | null): SelectedTrackState {
  const [aircraft, setAircraft] = useState<RichAircraft | null>(null);
  const [fetchedAtSec, setFetchedAtSec] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const inFlightRef = useRef(false);
  const reqIdRef = useRef(0);

  const applyAircraft = useCallback((raw: Aircraft) => {
    const rich = enrich(raw);
    setAircraft(rich);
    setFetchedAtSec(Math.floor(Date.now() / 1000));
  }, []);

  const tick = useCallback(async () => {
    if (!selectedIcao || inFlightRef.current) return;
    const reqId = ++reqIdRef.current;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    inFlightRef.current = true;
    try {
      const ac = await fetchAircraft(selectedIcao, ctrl.signal);
      if (ctrl.signal.aborted || reqId !== reqIdRef.current) return;
      applyAircraft(ac);
    } catch {
      // Keep showing the last successful track sample.
    } finally {
      if (reqId === reqIdRef.current) {
        inFlightRef.current = false;
        if (abortRef.current === ctrl) abortRef.current = null;
      }
    }
  }, [applyAircraft, selectedIcao]);

  useEffect(() => {
    reqIdRef.current++;
    abortRef.current?.abort();
    setAircraft(null);
    setFetchedAtSec(0);
    if (!selectedIcao) return;

    void tick();
    const id = setInterval(() => {
      if (!document.hidden) void tick();
    }, SELECTED_POLL_MS);
    const onVisible = () => {
      if (!document.hidden) void tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      abortRef.current?.abort();
    };
  }, [selectedIcao, tick]);

  return { aircraft, fetchedAtSec };
}

/** Replace one ICAO in a list with a fresher track sample (if present). */
export function mergeSelectedTrack(list: RichAircraft[], track: RichAircraft | null): RichAircraft[] {
  if (!track) return list;
  const idx = list.findIndex((a) => a.icao === track.icao);
  if (idx < 0) return [...list, track];
  const next = list.slice();
  next[idx] = track;
  return next;
}
